/**
 * Liste blanche des endpoints autorisés pour les commandes vocales
 * Chaque endpoint est associé à ses métadonnées de sécurité
 */

export interface EndpointConfig {
  method: 'GET' | 'POST' | 'DELETE';
  requiresConfirmation: boolean;
  minConfidence: number;
  description: string;
}

export const ALLOWED_ENDPOINTS: Record<string, EndpointConfig> = {
  'calendar.addPresences': {
    method: 'POST',
    requiresConfirmation: false,
    minConfidence: 0.7,
    description: 'Ajouter des présences au calendrier'
  },
  'calendar.removePresences': {
    method: 'DELETE',
    requiresConfirmation: true,
    minConfidence: 0.8,
    description: 'Supprimer des présences du calendrier'
  },
  'calendar.listPresences': {
    method: 'GET',
    requiresConfirmation: false,
    minConfidence: 0.6,
    description: 'Consulter les présences'
  },
  'project.createTask': {
    method: 'POST',
    requiresConfirmation: false,
    minConfidence: 0.7,
    description: 'Créer une tâche dans un projet'
  },
  'project.updateTaskStatus': {
    method: 'POST',
    requiresConfirmation: false,
    minConfidence: 0.7,
    description: 'Changer le statut d\'une tâche'
  },
  'project.deleteTask': {
    method: 'DELETE',
    requiresConfirmation: true,
    minConfidence: 0.8,
    description: 'Supprimer une tâche'
  },
  'project.createProject': {
    method: 'POST',
    requiresConfirmation: false,
    minConfidence: 0.7,
    description: 'Créer un nouveau projet'
  },
  'project.assignTask': {
    method: 'POST',
    requiresConfirmation: false,
    minConfidence: 0.7,
    description: 'Assigner une tâche à un membre'
  },
  'project.listMembers': {
    method: 'GET',
    requiresConfirmation: false,
    minConfidence: 0.7,
    description: 'Lister les membres d\'un projet'
  },
  /**
   * MOKA-207 Phase 4, Lot 1 : lecture des tâches assignées à l'utilisateur
   * courant, filtrées par échéance (`scope`). Consultation seule — pas de
   * confirmation requise.
   */
  'project.listMyTasks': {
    method: 'GET',
    requiresConfirmation: false,
    minConfidence: 0.6,
    description: 'Lister mes tâches (aujourd\'hui, en retard ou toutes)'
  },
  /**
   * MOKA-207 Phase 4, Lot 1 : synthèse des compteurs d'un projet par statut.
   */
  'project.projectSummary': {
    method: 'GET',
    requiresConfirmation: false,
    minConfidence: 0.6,
    description: 'Résumer l\'avancement d\'un projet'
  },
  /**
   * MOKA-207 Phase 4, Lot 1 : ajout d'un commentaire sur une tâche. Même
   * niveau de confiance que les autres écritures non destructives du module
   * (createTask, assignTask...).
   */
  'project.addComment': {
    method: 'POST',
    requiresConfirmation: false,
    minConfidence: 0.7,
    description: 'Ajouter un commentaire à une tâche'
  },
  /**
   * MOKA-207 Phase 4, Lot 1 : ajout d'un tag du catalogue projet à une tâche.
   */
  'project.addTagToTask': {
    method: 'POST',
    requiresConfirmation: false,
    minConfidence: 0.7,
    description: 'Ajouter une étiquette à une tâche'
  },
  /**
   * MOKA-207 Phase 4, Lot 1 : création d'une réclamation/incident matériel.
   * Whitelistée ici pour le handler inventaire porté en parallèle par un
   * autre agent — même contrat, non implémentée par ce lot.
   */
  'inventory.createClaim': {
    method: 'POST',
    requiresConfirmation: false,
    minConfidence: 0.7,
    description: 'Créer une réclamation sur un équipement'
  },
  /**
   * MOKA-207 Phase 4, Lot 1 : réservation d'une salle. Whitelistée ici pour
   * le handler calendrier/salles porté en parallèle par un autre agent —
   * même contrat, non implémentée par ce lot.
   */
  'rooms.reserveRoom': {
    method: 'POST',
    requiresConfirmation: false,
    minConfidence: 0.7,
    description: 'Réserver une salle'
  },
  /**
   * MOKA-207 Phase 4, Lot 3 : mémorisation de contexte conversationnel
   * (personne/groupe/release/échéance cités). No-op côté Edge Function —
   * résolu et persisté front par `SessionVoiceHandler` (même registre que les
   * autres `VoiceModuleHandler`, cf. `ServiceRouterService`). Tous les champs
   * sont optionnels.
   */
  'session.setContext': {
    method: 'POST',
    requiresConfirmation: false,
    minConfidence: 0.6,
    description: 'Mémoriser le contexte cité (personne, groupe, release, échéance)'
  },
  'session.dismiss': {
    method: 'POST',
    requiresConfirmation: false,
    minConfidence: 0.6,
    description: 'Ignorer le follow-up en cours'
  },
  /**
   * MOKA-206 Phase 3, Lot E : envoyé par le prompt GPT quand la demande ne
   * correspond à aucun endpoint connu — traité en no-op AVANT la validation
   * générique dans `VoiceService.executeIntent` (même position que
   * `session.dismiss`), message poli + suggestions au lieu de l'erreur
   * technique brute que `IntentValidatorService` produirait sinon.
   */
  'session.unsupported': {
    method: 'POST',
    requiresConfirmation: false,
    minConfidence: 0.5,
    description: 'Demande non reconnue — aucune action disponible'
  }
};

/**
 * Vérifie si un endpoint est autorisé
 */
export function isEndpointAllowed(endpoint: string): boolean {
  return endpoint in ALLOWED_ENDPOINTS;
}

/**
 * Récupère la configuration d'un endpoint
 */
export function getEndpointConfig(endpoint: string): EndpointConfig | null {
  return ALLOWED_ENDPOINTS[endpoint] || null;
}
